import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GlobalNav from '../components/GlobalNav';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../AuthContext';
import { getOrder, getUserDocuments, uploadDocument } from '../api';
import { Package, FileText, Upload } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user && orderId) {
      getOrder(orderId).then(res => setOrder(res.data)).catch(() => navigate('/dashboard'));
      getUserDocuments(orderId).then(res => setDocuments(res.data));
    }
  }, [user, orderId, navigate]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('order_id', orderId);
    formData.append('file', file);

    try {
      await uploadDocument(formData);
      toast.success('Document uploaded successfully');
      getUserDocuments(orderId).then(res => setDocuments(res.data));
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading || !order) {
    return <div className="min-h-screen bg-background flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="order-detail-page">
      <div className="container mx-auto px-4 md:px-8 py-6">
        <GlobalNav />
      </div>

      <div className="container mx-auto px-4 md:px-8 py-12">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-button">
            ← Back to Dashboard
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Order Details
                  </CardTitle>
                  <Badge className={getStatusColor(order.status)} data-testid="order-detail-status">
                    {order.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Service</p>
                    <p className="font-semibold text-lg" data-testid="order-detail-service-name">{order.service_name}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Order ID</p>
                      <p className="font-mono text-sm" data-testid="order-detail-id">{order.id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Amount</p>
                      <p className="font-semibold text-accent" data-testid="order-detail-amount">₹{order.amount.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Created</p>
                      <p className="text-sm">{format(new Date(order.created_at), 'MMM dd, yyyy HH:mm')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Last Updated</p>
                      <p className="text-sm">{format(new Date(order.updated_at), 'MMM dd, yyyy HH:mm')}</p>
                    </div>
                  </div>
                  {order.notes && (
                    <div>
                      <p className="text-sm text-muted-foreground">Notes</p>
                      <p className="text-sm">{order.notes}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="file-upload">
                      <Button variant="outline" className="w-full" asChild data-testid="upload-document-button">
                        <span className="cursor-pointer flex items-center justify-center gap-2">
                          <Upload className="h-4 w-4" />
                          Upload Document
                        </span>
                      </Button>
                    </label>
                    <input
                      id="file-upload"
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                      data-testid="file-upload-input"
                    />
                  </div>

                  {documents.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-8">No documents uploaded yet</p>
                  ) : (
                    <div className="space-y-2">
                      {documents.map(doc => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
                          data-testid={`order-document-${doc.id}`}
                        >
                          <div>
                            <p className="font-medium text-sm" data-testid={`order-document-name-${doc.id}`}>{doc.file_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(doc.uploaded_at), 'MMM dd, yyyy')}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm">Download</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Order Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    </div>
                    <div>
                      <p className="font-medium text-sm">Order Placed</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(order.created_at), 'MMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                  {order.status !== 'pending' && (
                    <div className="flex gap-3">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Payment Confirmed</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(order.updated_at), 'MMM dd, yyyy')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OrderDetailPage;